import { ReactNode, useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { Layout as AntLayout } from 'antd'

import { Permissions } from 'core/application/account/permissions'
import { UserRoles } from 'core/application/account/userRoles'
import { ProductIds } from 'core/application/commons/config'

import CompanyPersonalComponent, { CompanyPersonalSection } from 'common/components/layout/CompanyPersonalComponent'
import EmployeeDashboardHeader from 'common/components/layout/DashboardHeader'
import Footer from 'common/components/layout/Footer'

import { Button, Drawer, Icon, Menu, MenuItem } from 'common/components'

import useCurrentProduct from '../../common/hooks/useCurrentProduct'
import { useLoginUserV2 } from '../../common/hooks/useLoginUser'
import { ROUTE_PATHS } from '../../common/routePaths'

import './DashboardStyles.less'

const { Sider } = AntLayout

type AdminDashboardProps = {
  children?: ReactNode
  navigationMenu: MenuItem[]
}

const EmployeeDashboardWrapper = (props: AdminDashboardProps) => {
  const location = useLocation()
  const { user } = useLoginUserV2()
  const product = useCurrentProduct()
  const [activeKey, setActiveKey] = useState<string[] | undefined>()
  const { t: translate } = useTranslation('ee-dashboard')

  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false)
  const [initialLoad, setInitialLoad] = useState<boolean>(false)

  useEffect(() => {
    const pathname = location.pathname
    const found = props.navigationMenu.find((x) => x.path === pathname)
    setActiveKey(found ? [found.key.toString()] : [props.navigationMenu[0].key.toString()])
  }, [location])

  const companyPersonalSection = location.pathname.includes(ROUTE_PATHS.ADMIN_DASHBOARD_PATH)
    ? CompanyPersonalSection.Company
    : CompanyPersonalSection.Personal

  const adminDashboardMenuItems = props.navigationMenu
    .sort((first, second) => first.key - second.key)
    .map((item: MenuItem) => (
      <MenuItem key={item.key}>
        <Link to={item.path}>
          <Icon type={item.icon} />
          {translate(item.name)}{' '}
        </Link>
      </MenuItem>
    ))

  const getUserInfo = () => {
    return {
      firstName: user.firstName,
      lastName: user.lastName,
      isAdmin: user.roles.includes(UserRoles.CompanyAdmin) || user.roles.includes(UserRoles.CompanySecondaryAdmin),
    }
  }

  const getPersonalDashboardLink = () => {
    return user.permissions.includes(Permissions.AccessEligibleEmployeeDashboard)
      ? ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD
      : ROUTE_PATHS.NON_ELIGIBLE_EMPLOYEE_DASHBOARD
  }

  const personalSectionEnabled =
    user &&
    user.permissions.some(
      (p) => p === Permissions.AccessEligibleEmployeeDashboard || p === Permissions.AccessNonEligibleEmployeeDashboard,
    )
  const [openSider, setOpenSider] = useState(false)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const handleWindowResize = () => {
    setWindowWidth(window.innerWidth)
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize)
    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const handleDrawerPlacement = useCallback(() => {
    let desktopContainer = document.getElementById('desktop-nav-container')
    let mobileContainer = document.getElementById('mobile-nav-container')
    let navContainer = document.getElementById('nav-container')

    if (window.innerWidth > 992) {
      let children: any = desktopContainer.querySelector('.ant-layout-sider-children')
      children.append(navContainer)
    } else {
      mobileContainer.append(navContainer)
    }
  }, [])

  useEffect(() => {
    setInitialLoad(true)
    handleDrawerPlacement()
    window.addEventListener('resize', handleDrawerPlacement)

    return () => {
      window.removeEventListener('resize', handleDrawerPlacement)
    }
  }, [])

  let linkValue = ''
  switch (product.productId) {
    case ProductIds.Perks:
      linkValue = 'https://workperks.zendesk.com/hc/en-us'
      break
    case ProductIds.IchraId:
      linkValue = 'https://peoplekeephelpichra2.zendesk.com/hc/en-us'
      break
    case ProductIds.QsehraId:
      linkValue = 'https://peoplekeephelp.zendesk.com/hc/en-us'
      break
    default:
      linkValue = 'https://grouphra.zendesk.com/hc/en-us'
      break
  }
  const isUserAdmin =
    user.roles.includes(UserRoles.CompanyAdmin) || user.roles.includes(UserRoles.CompanySecondaryAdmin)

  return (
    <AntLayout data-testid={'ee-dashboard-page'}>
      <EmployeeDashboardHeader
        user={user}
        userInfo={{
          firstName: user.firstName,
          lastName: user.lastName,
          isAdmin: isUserAdmin,
        }}
        translate={translate}
        companyDashboardLink={ROUTE_PATHS.DASHBOARD_PATH}
        personalDashboardLink={getPersonalDashboardLink()}
        companyPersonalSection={companyPersonalSection}
        personalSectionEnabled={personalSectionEnabled}
        openSider={openSider}
        setOpenSider={setOpenSider}
        windowWidth={windowWidth}
      />
      <AntLayout className="dashboard_body" data-testid={'ee-dashboard-body'}>
        {initialLoad && (
          <DrawerButton>
            <Button
              onClick={() => {
                setIsDrawerVisible(true)
              }}
              className="header-drawer-toggle"
              type="text"
            >
              <Icon type="far fa-bars" fontAwesome />
            </Button>
          </DrawerButton>
        )}

        <Sider
          width={240}
          id="desktop-nav-container"
          className="nav-desktop-container"
          data-testid={'ee-dashboard-sider'}
        >
          <Menu selectedKeys={activeKey} mode="inline" id="nav-container" className="dashboard-nav">
            {adminDashboardMenuItems}
            <MenuItem key="help-link" className={'mt-24 drawer-help-center-link'}>
              <div className="pr-24">
                <Icon type="fa fa-question-circle" fontAwesome className="mr-8" />
                <span>{translate('user-header.help-center')}</span>
                <a href={linkValue} target={'_blank'} />
              </div>
            </MenuItem>
          </Menu>
        </Sider>

        <Drawer
          width={240}
          open={isDrawerVisible}
          onClose={() => {
            setIsDrawerVisible(false)
          }}
          placement="left"
          rootClassName="nav-mobile-drawer"
          forceRender
        >
          <div id="mobile-nav-container">
            {isUserAdmin ? (
              <div className="flex js-center mt-24 pk--drawer-role-toggler">
                <CompanyPersonalComponent
                  companyDashboardLink={ROUTE_PATHS.DASHBOARD_PATH}
                  personalDashboardLink={getPersonalDashboardLink()}
                  companyPersonalSection={companyPersonalSection}
                  personalSectionEnabled={personalSectionEnabled}
                />
              </div>
            ) : null}
          </div>
        </Drawer>

        <AntLayout className="dashboard-layout-content" data-testid={'ee-dashboard-content'}>
          {props.children}
        </AntLayout>
      </AntLayout>
      <Footer />
    </AntLayout>
  )
}

export interface MenuItem {
  name: string
  key: number
  path: string
  icon: string
  component: any
}

export default EmployeeDashboardWrapper

const DrawerButton = ({ children }: { children: JSX.Element }) => {
  return createPortal(children, document?.getElementById('header-navigation-button-container'))
}
